import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { PageTitle } from '@/components'
import { Button, Checkbox, DateTimePicker, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import {
  AVAILABILITY_TYPES,
  AvailabilityType,
  CONTINUOUS_QUEST_TYPES,
  ContinuousQuestSummary,
  QuestConfigDto,
  QuestDraft,
  QuestTranslationDto,
  QuestTypeKey,
  QuestUpsertDto,
  TOPUP_MODES_REQUIRING_MIN_AMOUNT,
  TagDto,
  continuousConflict,
  describeWindow,
  emptyQuestConfig,
  isAlwaysOpen,
} from '@/common/models/gamification'

import { TranslationTabs } from '../shared/TranslationTabs'
import { QuestConfigFields } from './QuestConfigFields'
import { TagsField } from './TagsField'

interface QuestEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Existing draft to edit (with its `id`), or null/undefined to author a new one. */
  quest?: QuestDraft | null
  onSubmit: (
    data: QuestUpsertDto,
    tags: TagDto[],
    id?: string,
  ) => Promise<void> | void
  /**
   * Active continuous quests across the whole festival edition, with their time windows. Used to
   * warn about a placement conflict before submit; the server enforces the rule for real.
   */
  continuousQuests?: ContinuousQuestSummary[]
  /** Festival edition id — threaded down to the continuous quest types' token-pool picker. */
  editionId: string
}

const emptyConfig: QuestConfigDto = emptyQuestConfig('TOPUP')

const toDate = (iso?: string | null): Date | null =>
  iso ? new Date(iso) : null

export const QuestEditorModal = ({
  open,
  onOpenChange,
  quest,
  onSubmit,
  continuousQuests = [],
  editionId,
}: QuestEditorModalProps) => {
  const isEditMode = !!quest

  const [availabilityType, setAvailabilityType] =
    useState<AvailabilityType>('GENERAL')
  const [points, setPoints] = useState<number>(0)
  const [start, setStart] = useState<Date | null>(null)
  const [end, setEnd] = useState<Date | null>(null)
  const [ordinal, setOrdinal] = useState<number>(0)
  const [active, setActive] = useState<boolean>(true)
  const [config, setConfig] = useState<QuestConfigDto>(emptyConfig)
  const [translations, setTranslations] = useState<QuestTranslationDto[]>([])
  const [tags, setTags] = useState<TagDto[]>([])
  const [saving, setSaving] = useState(false)

  // `HtmlEditor` is built around react-hook-form (its `Controller` needs a valid `control` + `name`),
  // but this modal is plain `useState` — the instructions HTML still flows through `TranslationTabs`'
  // `value`/`onFieldChange` like every other field. This form exists only to hand `HtmlEditor` a
  // `control`; nothing is ever registered or submitted through it. Same pattern as QuestGroupEditorModal.
  const { control: htmlEditorControl } = useForm()

  useEffect(() => {
    if (!open) return
    if (quest) {
      setAvailabilityType(quest.availabilityType)
      setPoints(quest.points ?? 0)
      setStart(toDate(quest.timeWindowStart))
      setEnd(toDate(quest.timeWindowEnd))
      setOrdinal(quest.ordinal ?? 0)
      setActive(quest.active ?? true)
      setConfig((quest.config as QuestConfigDto) ?? { ...emptyConfig })
      setTranslations(quest.translations ?? [])
      setTags(quest.tags ?? [])
    } else {
      setAvailabilityType('GENERAL')
      setPoints(0)
      setStart(null)
      setEnd(null)
      setOrdinal(0)
      setActive(true)
      setConfig({ ...emptyConfig })
      setTranslations([])
      setTags([])
    }
  }, [open, quest])

  const isContinuous = CONTINUOUS_QUEST_TYPES.includes(config.typeKey)
  // Continuous + the interactive terminals (AVATAR_SELECT, QR_SCANNING) all complete/earn once-ever,
  // so availability is locked to GENERAL. Only continuous hides the fixed-points field (their points
  // are computed per event, not fixed).
  const isOnceEverInteractive =
    config.typeKey === 'AVATAR_SELECT' || config.typeKey === 'QR_SCANNING'
  const requiresGeneral = isContinuous || isOnceEverInteractive

  const windowInvalid = !!start && !!end && end <= start

  // One continuous quest of each type may be OPEN AT A TIME per festival edition — not one in total.
  // A second is fine with a non-overlapping window; one with no dates at all is live throughout and
  // therefore exclusive. Mirrors ContinuousQuestPlacementValidator; the server still has final say.
  const conflict = continuousConflict(
    continuousQuests,
    config.typeKey,
    start ? start.toISOString() : null,
    end ? end.toISOString() : null,
    quest?.id,
  )
  const minAmountInvalid =
    config.typeKey === 'TOPUP' &&
    TOPUP_MODES_REQUIRING_MIN_AMOUNT.includes(config.mode) &&
    (config.minAmount === undefined || config.minAmount <= 0)
  const pointsPerUnitInvalid =
    (config.typeKey === 'CONTINUOUS_TOPUP' ||
      config.typeKey === 'CONTINUOUS_SPENDING') &&
    (config.pointsPerUnit === undefined || config.pointsPerUnit <= 0)
  const avatarInvalid =
    config.typeKey === 'AVATAR_SELECT' &&
    ((config.options?.length ?? 0) < 2 ||
      !config.options.every(
        (o) =>
          !!o.photoRef &&
          Object.values(o.labels ?? {}).some((l) => !!l && l.trim() !== ''),
      ))
  const qrInvalid =
    config.typeKey === 'QR_SCANNING' &&
    (config.latitude === undefined ||
      config.latitude < -90 ||
      config.latitude > 90 ||
      config.longitude === undefined ||
      config.longitude < -180 ||
      config.longitude > 180 ||
      config.validationRadius === undefined ||
      config.validationRadius <= 0)
  const invalid =
    points < 0 ||
    windowInvalid ||
    !!conflict ||
    minAmountInvalid ||
    pointsPerUnitInvalid ||
    avatarInvalid ||
    qrInvalid

  const handleClose = () => onOpenChange(false)

  // Once-ever types are always GENERAL; continuous types additionally have no fixed points. Pin those
  // when switching type.
  const handleConfigChange = (next: QuestConfigDto) => {
    if (
      next.typeKey === 'AVATAR_SELECT' ||
      next.typeKey === 'QR_SCANNING' ||
      CONTINUOUS_QUEST_TYPES.includes(next.typeKey)
    ) {
      setAvailabilityType('GENERAL')
    }
    if (CONTINUOUS_QUEST_TYPES.includes(next.typeKey)) {
      setPoints(0)
    }
    setConfig(next)
  }

  const submit = async () => {
    if (invalid) return
    const data: QuestUpsertDto = {
      availabilityType,
      points: isContinuous ? 0 : points,
      timeWindowStart: start ? start.toISOString() : null,
      timeWindowEnd: end ? end.toISOString() : null,
      ordinal,
      active,
      config,
      translations,
    }
    try {
      setSaving(true)
      await onSubmit(data, tags, quest?.id)
      handleClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="events-star"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Quest</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={submit}
            type="button"
            variant="primary"
            disabled={invalid || saving}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <SelectField
            label="Availability"
            required
            value={availabilityType}
            onValueChange={(v) => setAvailabilityType(v as AvailabilityType)}
            options={(requiresGeneral
              ? (['GENERAL'] as AvailabilityType[])
              : AVAILABILITY_TYPES
            ).map((a) => ({ value: a, label: a }))}
          />
          {requiresGeneral && (
            <span className="mt-1 block text-xs text-monochrome-2">
              {config.typeKey} is always GENERAL (once-ever).
            </span>
          )}
        </div>

        {!isContinuous && (
          <FormGroup label="Points">
            <Input
              type="number"
              min="0"
              value={points}
              isInvalid={points < 0}
              onChange={(e) => setPoints(Number(e.target.value))}
            />
          </FormGroup>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <DateTimePicker
              label="Time window start"
              mode="datetime"
              value={start}
              onChange={setStart}
            />
          </div>
          <div>
            <DateTimePicker
              label="Time window end"
              mode="datetime"
              value={end}
              onChange={setEnd}
              hasError={windowInvalid}
            />
            {windowInvalid && (
              <span className="text-xs text-accent-3">
                End must be after start
              </span>
            )}
          </div>
        </div>

        {conflict && (
          <div className="rounded-lg border border-accent-3 p-3 text-xs text-accent-3">
            {isAlwaysOpen(conflict.timeWindowStart, conflict.timeWindowEnd) ||
            (!start && !end) ? (
              <>
                A {config.typeKey} with no start and no end date is live for the
                whole edition, so it is the only one the edition can have.{' '}
                <strong>{conflict.title ?? 'An existing quest'}</strong>
                {conflict.roadmapTitle ? ` in "${conflict.roadmapTitle}"` : ''}{' '}
                already covers {describeWindow(conflict.timeWindowStart, conflict.timeWindowEnd)}.
                Give both quests explicit, non-overlapping time windows to run
                them side by side.
              </>
            ) : (
              <>
                This time window overlaps{' '}
                <strong>{conflict.title ?? 'an existing quest'}</strong>
                {conflict.roadmapTitle ? ` in "${conflict.roadmapTitle}"` : ''} (
                {describeWindow(conflict.timeWindowStart, conflict.timeWindowEnd)}
                ). Only one {config.typeKey} may be open at a time in this
                festival edition — adjust the dates so they do not overlap.
              </>
            )}
          </div>
        )}

        <FormGroup label="Order">
          <Input
            type="number"
            min="0"
            value={ordinal}
            onChange={(e) => setOrdinal(Number(e.target.value))}
          />
        </FormGroup>

        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={active}
            onCheckedChange={(checked) => setActive(!!checked)}
          />
          <span className="text-body-medium text-monochrome-1">Active</span>
        </label>

        <QuestConfigFields
          value={config}
          onChange={handleConfigChange}
          deepLink={quest?.deepLink}
          questId={quest?.id}
          editionId={editionId}
        />

        <TagsField editionId={editionId} value={tags} onChange={setTags} />

        <div className="border-t border-monochrome-120 pt-6">
          <div className="text-lg font-bold text-monochrome-1 mb-3">
            Translations
          </div>
          <TranslationTabs<QuestTranslationDto>
            value={translations}
            onChange={setTranslations}
            makeEmpty={(locale) => ({
              locale,
              title: '',
              description: '',
              instructions: '',
            })}
            renderFields={({ value, locale, onFieldChange }) => (
              <div className="space-y-4">
                <FormGroup label="Title">
                  <Input
                    value={value.title ?? ''}
                    onChange={(e) => onFieldChange({ title: e.target.value })}
                  />
                </FormGroup>
                <FormGroup label="Description">
                  <Input
                    value={value.description ?? ''}
                    onChange={(e) =>
                      onFieldChange({ description: e.target.value })
                    }
                  />
                </FormGroup>
                <HtmlEditor
                  // Same rich-text authoring as a quest group's instructions: this field is HTML
                  // rendered by the client apps (its column is `text` for that reason), so it must
                  // not be a plain single-line input. `key` keeps each locale's editor independent;
                  // `index` only feeds the never-enabled preview iframe's title, so it is inert.
                  key={locale}
                  label="Instructions"
                  index={0}
                  name={`quest-instructions.${locale}`}
                  variables={[]}
                  control={htmlEditorControl}
                  value={value.instructions ?? ''}
                  onChange={(data) => onFieldChange({ instructions: data })}
                  isError={false}
                />
              </div>
            )}
          />
        </div>
      </div>
    </Modal>
  )
}
